import type { ReactNode } from 'react';

import { Button, ColorSchemeSwitch } from '../shared/components';

import styles from './AppShell.module.css';

/**
 * Frame around the signed-in screens: brand, navigation, sign out.
 *
 * A sidebar, as the approved mockup lays it out — a rail of destinations
 * beside the content, not a row of tabs above it. Below 60rem the rail becomes
 * a scrolling top bar so every destination stays reachable (§8.7).
 *
 * Navigation is still a list of buttons rather than a URL router. There are a
 * handful of destinations and no deep links to support yet; a router arrives
 * when a link into a screen has to survive being pasted.
 */

export interface ShellTab {
  readonly id: string;
  readonly labelAr: string;
  /** Shown as a count beside the label, e.g. outstanding fields. */
  readonly badge?: number;
}

export interface AppShellProps {
  readonly tabs: readonly ShellTab[];
  readonly activeTabId: string;
  readonly employeeName: string;
  readonly onSelectTab: (id: string) => void;
  readonly onSignOut: () => void;
  readonly children: ReactNode;
}

/** First letter, for the account chip. Purely decorative; the name is beside it. */
function initial(name: string): string {
  return name.trim().charAt(0);
}

export function AppShell({
  tabs,
  activeTabId,
  employeeName,
  onSelectTab,
  onSignOut,
  children,
}: AppShellProps) {
  return (
    <div className={styles.shell}>
      <div className={styles.layout}>
        <div className={styles.sidebar}>
          <div className={styles.topBar}>
            <Brand />
            <Account name={employeeName} onSignOut={onSignOut} compact />
          </div>

          <div className={styles.desktopBrand}>
            <Brand />
          </div>

          <nav className={styles.nav} aria-label="أقسام النظام">
            <ul className={styles.navList}>
              {tabs.map((tab) => {
                const current = tab.id === activeTabId;

                return (
                  <li key={tab.id}>
                    <button
                      type="button"
                      /* §8.7 — the current location is announced, not just
                         coloured. */
                      aria-current={current ? 'page' : undefined}
                      className={current ? styles.tabActive : styles.tab}
                      onClick={() => {
                        onSelectTab(tab.id);
                      }}
                    >
                      {tab.labelAr}
                      {tab.badge !== undefined && tab.badge > 0 ? (
                        <span
                          className={styles.badge}
                          aria-label={`${String(tab.badge)} متبقٍ`}
                        >
                          {tab.badge}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className={styles.desktopAccount}>
            <ColorSchemeSwitch fullWidth />
            <Account name={employeeName} onSignOut={onSignOut} />

            {/* Which build this is, so a report of "it still does X" can be
                checked against what is actually deployed. */}
            <p className={styles.version}>الإصدار {__APP_VERSION__}</p>
          </div>
        </div>

        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div className={styles.brand}>
      <span className={styles.mark}>
        {/* Official symbol asset, unmodified (brand addon §8). Decorative here:
            the school's name is spelt out beside it. */}
        <img src="/logo-png-pic.png" alt="" className={styles.logo} />
      </span>

      <span className={styles.brandText}>
        <span className={styles.brandTitle}>صندوق التأمين الصحي</span>
        <span className={styles.brandSubtitle}>مدارس الرشيد الحديثة</span>
      </span>
    </div>
  );
}

function Account({
  name,
  onSignOut,
  compact = false,
}: {
  readonly name: string;
  readonly onSignOut: () => void;
  readonly compact?: boolean;
}) {
  return (
    <div className={styles.account}>
      <span className={styles.avatar} aria-hidden="true">
        {initial(name)}
      </span>
      {compact ? null : <span className={styles.accountName}>{name}</span>}
      <Button variant="ghost" size="sm" onClick={onSignOut}>
        خروج
      </Button>
    </div>
  );
}
